import axiosInstance from './axiosInstance';

export interface FeedAction {
  actionId: string;
  backendId: string;
  text: string;
  endIcon: string;
  uri: string;
  variant: string;
}

export interface FeedModel {
  applicationId: string;
  organisationId: string;
  subscriptionId: string;
  userId: string;
  title: string;
  description: string;
  correlationId: string;
  /** "information" (default) or "activity" (appears in Navigator Actions Required) */
  type?: string;
  /** Topic ID for grouping (e.g. "Access.NarcisEvo.TaskApproval") */
  topicId?: string;
  coPilotPrompt?: string;
  coPilotPromptAgent?: string;
  actions?: FeedAction[];
  linkText?: string;
  linkDesktopUrl?: string;
  linkMobileUrl?: string;
  dueDate?: string;
}

export interface FeedResponse {
  resultContent: string;
  resultStatus: string;
  originalJsonPayload: string;
}

// Backend response might use different casing depending on version
interface RawFeedResponse {
  // New format (camelCase)
  resultContent?: string;
  resultStatus?: string;
  originalJsonPayload?: string;
  // Old format (PascalCase)
  Status?: string;
  Content?: string;
  Payload?: string;
  // Lowercase format (current backend)
  status?: string;
  content?: string;
  payload?: string;
}

// Normalize response to handle all API response formats
const normalizeResponse = (data: RawFeedResponse): FeedResponse => {
  return {
    resultStatus: data.resultStatus || data.Status || data.status || '',
    resultContent: data.resultContent || data.Content || data.content || '',
    originalJsonPayload: data.originalJsonPayload || data.Payload || data.payload || '',
  };
};

export interface FeedLookupUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  workspaceId: string | null;
}

export interface FeedLookupSubscription {
  id: string;
  applicationId: string;
  name: string;
  users: FeedLookupUser[];
}

export interface FeedLookupOrganisation {
  id: string;
  name: string;
  subscriptions: FeedLookupSubscription[];
}

export interface FeedLookupResponse {
  organisations: FeedLookupOrganisation[];
}

export const feedService = {
  async sendFeedItem(feedModel: FeedModel): Promise<FeedResponse> {
    const response = await axiosInstance.post('/api/feed/send', feedModel);
    return normalizeResponse(response.data);
  },

  async deleteFeedItem(feedModel: FeedModel): Promise<FeedResponse> {
    const response = await axiosInstance.delete('/api/feed/delete', { data: feedModel });
    return normalizeResponse(response.data);
  },

  async getLookupData(): Promise<FeedLookupResponse> {
    const response = await axiosInstance.get<FeedLookupResponse>('/api/feed/lookup');
    return response.data;
  }
};
